// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  QuarantineResolutionBlockReason,
  QuarantineResolutionKind,
  QuarantineResolutionOption,
  QuarantinedSerial,
  Shipment,
} from '@/shipment/api/types';
import { QuarantineCorrectSerialDialog } from '@/shipment/components/quarantine-correct-serial-dialog';
import { QuarantineCreateAssetDialog } from '@/shipment/components/quarantine-create-asset-dialog';
import { QuarantineDuplicateSerialDialog } from '@/shipment/components/quarantine-duplicate-serial-dialog';
import { QuarantineResolveNoteDialog } from '@/shipment/components/quarantine-resolve-note-dialog';
import { Button, Stack, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';

const RESOLUTION_LABELS: Record<QuarantineResolutionKind, string> = {
  KEPT_HARDFIN_ITEM: 'Keep Hardfin item',
  CORRECTED_SERIAL: 'Correct serial',
  CREATED_ASSET: 'Create new asset',
  ACCEPTED_SERIAL: 'Accept serial',
};

// A serial reported twice, or one Hardfin already has.
function isDuplicateType(
  quarantineType: QuarantinedSerial['quarantineType']
): boolean {
  return (
    quarantineType === 'DUPLICATE_IN_ORDER' ||
    quarantineType === 'DUPLICATE_ALREADY_EXISTS'
  );
}

/** Names the action for the flow this hold opens. */
function toResolutionLabel(
  kind: QuarantineResolutionKind,
  quarantineType: QuarantinedSerial['quarantineType']
): string {
  if (kind === 'CORRECTED_SERIAL') {
    if (isDuplicateType(quarantineType)) {
      return 'Specify alternate serial';
    }
    if (quarantineType === 'SERIAL_PATTERN_MISMATCH') {
      return 'Specify alternate serial';
    }
    // Nothing to specify an alternate to; the unit arrived with no serial.
    if (quarantineType === 'MISSING_SERIAL') {
      return 'Specify new serial';
    }
  }
  if (kind === 'CREATED_ASSET' && isDuplicateType(quarantineType)) {
    return 'Accept and create';
  }
  return RESOLUTION_LABELS[kind];
}

/** Picks the action the hold points at, or none when the type has no preference. */
function pickRecommendedKind(
  held: QuarantinedSerial
): QuarantineResolutionKind | null {
  // Nothing identifies the unit, so naming it is the only way through.
  if (held.quarantineType === 'MISSING_SERIAL') {
    return 'CORRECTED_SERIAL';
  }
  if (!isDuplicateType(held.quarantineType)) {
    return null;
  }
  const create = held.resolutionOptions.find(
    (option) => option.kind === 'CREATED_ASSET'
  );
  // Once the serial is spoken for, an alternate one is the only way through.
  return create !== undefined && create.isEnabled
    ? 'CREATED_ASSET'
    : 'CORRECTED_SERIAL';
}

// Recommended, then the rest, then whatever cannot be run.
function toActionRank(
  option: QuarantineResolutionOption,
  recommended: QuarantineResolutionKind | null
): number {
  if (!option.isEnabled) {
    return 2;
  }
  return option.kind === recommended ? 0 : 1;
}

// Why an action the hold's type allows cannot be run right now.
const DISABLED_REASONS: Record<QuarantineResolutionBlockReason, string> = {
  SERIAL_ALREADY_TAKEN:
    'Hardfin already tracks an asset with this serial, so a new asset cannot be created with the same serial',
};

// Spacing units, wide enough for the longest action label.
const RESOLUTION_BUTTON_WIDTH_UNITS = 23;

const CORRECT_SERIAL_TOOLTIP =
  'Provide a new serial to have Hardfin treat this as a different asset and explicitly ignore the serial sent from the 3PL';

const CREATE_ASSET_TOOLTIP =
  'Confirm the device that the 3PL reported is genuinely new to Hardfin and create the asset';

const ACCEPT_AND_CREATE_TOOLTIP =
  'Confirm the serial the 3PL reported on this line item is valid and create a new asset for it, even though a duplicate line item exists in this shipment';

const KEEP_MATCHING_ITEM_TOOLTIP =
  'Confirm the device Hardfin already has under this serial is the one that arrived, and move it onto this shipment instead of creating a second one';

const ACCEPT_SERIAL_TOOLTIP =
  "Confirm the serial the 3PL reported is valid and create a new asset for it, even though the serial does not match the organization's serial patterns";

/** Builds the tooltip for a resolution. */
function toResolutionTooltip(
  kind: QuarantineResolutionKind,
  held: QuarantinedSerial
) {
  if (kind === 'CORRECTED_SERIAL') {
    return CORRECT_SERIAL_TOOLTIP;
  }
  if (kind === 'CREATED_ASSET') {
    return isDuplicateType(held.quarantineType)
      ? ACCEPT_AND_CREATE_TOOLTIP
      : CREATE_ASSET_TOOLTIP;
  }
  if (kind === 'ACCEPTED_SERIAL') {
    return ACCEPT_SERIAL_TOOLTIP;
  }

  // The keep action is only offered when Hardfin has the serial on record.
  if (held.existingItem === null) {
    return '';
  }
  // On a duplicate it settles the unit, whatever the items say.
  if (held.existingItem.id === held.reportedItem.id) {
    return KEEP_MATCHING_ITEM_TOOLTIP;
  }
  return `Treat Hardfin's item data (${held.existingItem.sku}, ${held.existingItem.name}) as authoritative and explicitly ignore the item information sent from the 3PL (${held.reportedItem.sku}, ${held.reportedItem.name})`;
}

/** Builds the tooltip for one action, disabled or not. */
function toOptionTooltip(
  option: QuarantineResolutionOption,
  held: QuarantinedSerial
): string {
  if (!option.isEnabled) {
    return option.disabledReason === null
      ? 'This action cannot be run on this hold right now'
      : DISABLED_REASONS[option.disabledReason];
  }
  return toResolutionTooltip(option.kind, held);
}

// The resolutions a duplicate hold answers with.
const DUPLICATE_KINDS: QuarantineResolutionKind[] = [
  'CORRECTED_SERIAL',
  'CREATED_ASSET',
];

function isDuplicateKind(
  kind: QuarantineResolutionKind
): kind is 'CORRECTED_SERIAL' | 'CREATED_ASSET' {
  return DUPLICATE_KINDS.includes(kind);
}

interface Props {
  held: QuarantinedSerial;
  shipmentKey: string;
  isInbound: boolean;
  originLocation: Shipment['fromLocation'];
  // Where an inbound shipment was received to.
  receiveLocation: Shipment['toLocation'];
  // The created asset's inventory date.
  inventoryDate: DateTime | null;
  // Serials already on the shipment; excluded from the correct-serial picker.
  shipmentSerials: string[];
}

/** The resolution actions for one open held serial. */
export function QuarantineResolutionActions(props: Props) {
  const [active, setActive] = useState<QuarantineResolutionKind | null>(null);
  // Kept past the close so a fading dialog keeps its copy.
  const [lastActive, setLastActive] =
    useState<QuarantineResolutionKind>('CORRECTED_SERIAL');

  const openDialogFor = (kind: QuarantineResolutionKind) => {
    setLastActive(kind);
    setActive(kind);
  };

  // Only a purchase order brings the unit into existence.
  const creationLocation =
    props.held.orderType === 'PURCHASE_ORDER'
      ? props.receiveLocation
      : props.originLocation;

  // A duplicate gets its own dialogs.
  const isDuplicate = isDuplicateType(props.held.quarantineType);

  // A purchase receive is what brings a unit into existence, so its correction
  // types a serial rather than picking an asset Hardfin already has.
  const isPurchaseReceive = props.held.orderType === 'PURCHASE_ORDER';

  const recommended = pickRecommendedKind(props.held);
  const actions = [...props.held.resolutionOptions].sort(
    (a, b) => toActionRank(a, recommended) - toActionRank(b, recommended)
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        marginTop={0.5}
        justifyContent="flex-start"
      >
        {actions.map((option) => (
          <Tooltip
            key={option.kind}
            arrow
            describeChild
            title={toOptionTooltip(option, props.held)}
          >
            {/* A disabled button fires no events and leaves the tab order, so
                the wrapper both carries the tooltip and takes the focus that
                surfaces it. It stretches so the button inside can size to the
                column rather than to its own label. */}
            <span
              tabIndex={option.isEnabled ? undefined : 0}
              style={{ display: 'flex' }}
            >
              <Button
                variant={option.kind === recommended ? 'contained' : 'outlined'}
                size="small"
                disabled={!option.isEnabled}
                onClick={() => openDialogFor(option.kind)}
                // Every action is the same width, whatever its label.
                sx={{
                  minWidth: (theme) =>
                    theme.spacing(RESOLUTION_BUTTON_WIDTH_UNITS),
                  whiteSpace: 'nowrap',
                }}
              >
                {toResolutionLabel(option.kind, props.held.quarantineType)}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Stack>

      <QuarantineDuplicateSerialDialog
        isOpen={isDuplicate && active !== null && isDuplicateKind(active)}
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        kind={isDuplicateKind(lastActive) ? lastActive : 'CORRECTED_SERIAL'}
        creationLocation={creationLocation}
        inventoryDate={props.inventoryDate}
        onClose={() => setActive(null)}
        onCorrectSerialInstead={() => openDialogFor('CORRECTED_SERIAL')}
      />
      <QuarantineCorrectSerialDialog
        isOpen={active === 'CORRECTED_SERIAL' && !isPurchaseReceive}
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        isInbound={props.isInbound}
        originLocation={props.originLocation}
        shipmentSerials={props.shipmentSerials}
        onClose={() => setActive(null)}
      />
      <QuarantineResolveNoteDialog
        isOpen={active === 'KEPT_HARDFIN_ITEM' && !isDuplicate}
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        kind="KEPT_HARDFIN_ITEM"
        isInbound={props.isInbound}
        onClose={() => setActive(null)}
      />
      <QuarantineCreateAssetDialog
        isOpen={active === 'CREATED_ASSET' && !isDuplicate}
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        kind="CREATED_ASSET"
        creationLocation={creationLocation}
        inventoryDate={props.inventoryDate}
        onClose={() => setActive(null)}
        onCorrectSerialInstead={() => openDialogFor('CORRECTED_SERIAL')}
      />
      <QuarantineCreateAssetDialog
        isOpen={
          active === 'CORRECTED_SERIAL' && isPurchaseReceive && !isDuplicate
        }
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        kind="CORRECTED_SERIAL"
        creationLocation={creationLocation}
        inventoryDate={props.inventoryDate}
        onClose={() => setActive(null)}
      />
      <QuarantineCreateAssetDialog
        isOpen={active === 'ACCEPTED_SERIAL'}
        shipmentKey={props.shipmentKey}
        quarantinedSerial={props.held}
        kind="ACCEPTED_SERIAL"
        creationLocation={creationLocation}
        inventoryDate={props.inventoryDate}
        onClose={() => setActive(null)}
        onCorrectSerialInstead={() => openDialogFor('CORRECTED_SERIAL')}
      />
    </>
  );
}
